// Libs
import { isNil, uniqBy } from "lodash";
import moment from "moment";
import { v4 as uuid } from "uuid";

// API
import type { TriggerRuleAverage } from "src/api/marketing-actions";

// Types
import { type Channel, ChannelCategory, type ChannelSourceEnum } from "src/types/channel";
import { AutoRulesComparePeriodEnum } from "src/types/enum/AutoRulesComparePeriodEnum";
import type { IdName } from "src/types/IdName";
import { OfferStatus, ServiceTime } from "src/types/offerSlot";
import { ThirdServiceCategory } from "src/types/posCredential";
import { type TriggerMetaData, TriggerMetricEnum } from "src/types/trigger";
import { TriggerOperatorEnum } from "src/types/trigger/triggerOperatorEnum";

// Utils
import { ALL_DAYS_OF_THE_WEEK } from "src/types/enum/DayOfTheWeek";
import {
    extendMarketingActionWithMainScenario,
    FORMAT_DATE_STD,
    formatDurationToTime,
    getCorrectTrigger,
    getDateStringWithTimeZone,
    getNowWithTimeZone,
    METRIC_FACTOR,
    PRICE_FACTOR,
} from "src/utils/formatters";
import { formatHoursToSlot, formatSlotToDuration } from "src/utils/hourSlots";
import { getFilledTrigger } from "src/views/marketing-actions/marketing-actions-create-view/MarketingAction.utils";
import {
    buildBaseCurrentKey,
    getCorrectPosValue,
    optionsCharacteristics,
    type RecurrenceConfig,
    recurrenceConfig,
} from "./MarketingActionCreateView.logic";

// Components
import type { OptionsObject, SnackbarKey, SnackbarMessage } from "notistack";
import type { TFunction } from "src/design-system/core/TranslationContext";
import { COMMON_TRANSLATION_NAMESPACE } from "src/i18n/config";
import type { MarketingActionScenario } from "src/types/action/MarketingActionScenario";
import { PushTriggerModeEnum } from "src/types/enum/PushTriggerModeEnum";
import { type Offer, OfferTypeEnum } from "src/types/offer";
import type { OfferScenario } from "src/types/offer/OfferScenario";
import type { Pos } from "src/types/pos";
import type {
    SpecificValueAutoRulePayload,
    SpecificValueSmartSchedulerPayload,
} from "src/types/specificValuePayload";
import type { SponsoredAd } from "src/types/sponsored-ads";
import type { SponsoredAdScenario } from "src/types/sponsored-ads/SponsoredAdScenario";
import { PositionEnum } from "src/types/trigger/TriggerPositionEnum";
import type { User } from "src/types/user";
import { MarketingActionActivationTypeEnum } from "src/utils/activationTypes";
import { addHundredDelta, subtractHundredDelta } from "src/utils/trigger";
import { MarketingActionVariantEnum } from "src/utils/variants";
import { UNKNOWN_CATEGORY_NAME } from "src/views/marketing-actions/components/form/OfferCharacteristicCard/OfferCharacteristicSharedInput";
import { MAX_EDGE_VALUE } from "src/views/marketing-actions/components/form/ScenarioCard/AddScenarioDialog";
import {
    type CustomScenario,
    NO_SCENARIO_ID,
} from "src/views/marketing-actions/components/form/ScenarioCard/ScenarioCard";
import {
    BudgetComputeMethodEnum,
    type ChannelSource,
    type MarketingActionConditionInput,
    type MarketingActionFormInput,
    type MarketingActionInputValues,
    type MarketingActionModalInput,
    type MarketingActionOtherInput,
    type MarketingActionRecurrenceInput,
    type MarketingActionScenarioCharacteristicInput,
    type MarketingActionServiceChannelInput,
    type OfferScenarioCharacteristicInput,
    type SponsoredAdScenarioCharacteristicInput,
} from "src/views/marketing-actions/marketing-actions-create-view/types/MarketingActionFormInput";
import type {
    MarketingActionFormOutput,
    MarketingActionScenarioCharacteristicOutput,
    MarketingActionScenarioOutput,
    OfferFormOutput,
    SponsoredAdFormOutput,
    TriggerCharacteristicOutput,
    TriggerStatus,
} from "src/views/marketing-actions/marketing-actions-create-view/types/MarketingActionFormOutput";
import type { MarketingActionErrorsModalEnum } from "src/views/sponsored-ads/routes";
import { RecurrenceFrequence, WEEKDAYS_PER_FREQUENCE } from "../components/form";

export enum OfferDiscountType {
    FlatDiscount = "flat",
    PercentDiscount = "percent",
}

const LIGHTSPEED_RESTRICTION = [5, 6, 7, 8];

// #region FUNCTION USE TO RESET FIELDS
interface InputOptions {
    variants: MarketingActionVariantEnum[];
    channelSources: ChannelSource[];
    t: TFunction;
    translateCommon?: TFunction;
    user: User;
    notDiscountAvailableIndexList?: number[];
    channelIds: number[];
    duplicate?: boolean;
    update?: boolean;
    channels?: Channel[];
    setFieldValue?: any;
    enqueueSnackbar?: (message: SnackbarMessage, options?: OptionsObject) => SnackbarKey;
    isStaticPeriodActivated?: boolean;
}

export interface OutputOptions {
    posListOptions: IdName[];
    allOption: IdName;
    channelSources: ChannelSource[];
    blockPreorder: boolean;
    t: TFunction;
    translateCommon?: TFunction;
    setFieldValue?: any;
    enqueueSnackbar?: (message: SnackbarMessage, options?: OptionsObject) => SnackbarKey;
    setMarketingActionErrorsModal: (error: MarketingActionErrorsModalEnum) => void;
    redirect: () => void;
    variants: MarketingActionVariantEnum[];
}

export function setDefaultMetaDataStartAndEndComparePeriodDate(): {
    startDateComparePeriod: string;
    endDateComparePeriod: string;
} {
    return {
        startDateComparePeriod: moment()
            .subtract(4, "weeks")
            .startOf("isoWeek")
            .format(FORMAT_DATE_STD),
        endDateComparePeriod: moment()
            .subtract(1, "weeks")
            .endOf("isoWeek")
            .format(FORMAT_DATE_STD),
    };
}

export function defaultMetaDataValues(channelIds: number[]): TriggerMetaData {
    return {
        value: null,
        channelIds: channelIds,
        thresholdFactor: 0,
    };
}

export function defaultValuesForTriggers(
    channelIds: number[],
    isPriceExvat: boolean
): MarketingActionConditionInput {
    return {
        triggerStart: {
            metaData: defaultMetaDataValues(channelIds),
            metric: isPriceExvat ? TriggerMetricEnum.DailySalesExvat : TriggerMetricEnum.DailySales,
            operator: TriggerOperatorEnum.LessThan,
            comparePeriod: AutoRulesComparePeriodEnum.lastFourWeeks,
        },
        triggerStop: {
            metaData: defaultMetaDataValues(channelIds),
            metric: isPriceExvat ? TriggerMetricEnum.DailySalesExvat : TriggerMetricEnum.DailySales,
            operator: TriggerOperatorEnum.MoreThan,
            comparePeriod: AutoRulesComparePeriodEnum.lastFourWeeks,
        },
        triggerStopStatus: "disable",
        triggerStartStatus: "enable",
    };
}
// #endregion

//#region INPUTS

export const inputFormatPos = (values: MarketingActionInputValues): Pos[] => {
    return values === null ? [] : values.posMarketingActions.map((e) => e.pos);
};

export const inputFormatOriginalPos = (values: MarketingActionInputValues): number[] | null => {
    return values === null ? null : values.posMarketingActions.map((e) => e.pos.id);
};

export const inputFormatScenarioBasicConfig = (
    currentValues: MarketingActionInputValues,
    options: InputOptions,
    isPricesExvat: boolean
): MarketingActionFormInput["scenarioBasicConfig"] => {
    const firstScenario = currentValues?.scenarios[0];
    const trigger = firstScenario?.triggerStart;

    const defaultPeriod = AutoRulesComparePeriodEnum.lastFourWeeks;

    const defaultChannelId =
        (options.user as any).organizationService?.service === ThirdServiceCategory.Lightspeed &&
        options.variants.includes(MarketingActionVariantEnum.SmartScheduler)
            ? options?.channelIds.filter((elm) => LIGHTSPEED_RESTRICTION.includes(elm))
            : options?.channelIds;

    const defaultStartAndEndComparePeriodDate = setDefaultMetaDataStartAndEndComparePeriodDate();
    const startAndEndComparePeriodDate = {
        startDateComparePeriod: trigger?.metaData?.startDateComparePeriod
            ? moment(trigger?.metaData?.startDateComparePeriod).format(FORMAT_DATE_STD)
            : defaultStartAndEndComparePeriodDate.startDateComparePeriod,
        endDateComparePeriod: trigger?.metaData?.endDateComparePeriod
            ? moment(trigger?.metaData?.endDateComparePeriod).format(FORMAT_DATE_STD)
            : defaultStartAndEndComparePeriodDate.endDateComparePeriod,
    };

    const defaultMetric = isPricesExvat
        ? TriggerMetricEnum.DailySalesExvat
        : TriggerMetricEnum.DailySales;

    const scenarioBasicConfig: MarketingActionFormInput["scenarioBasicConfig"] = {
        comparePeriod: trigger ? trigger.comparePeriod : defaultPeriod,
        channelIds: trigger?.metaData?.channelIds
            ? trigger?.metaData?.channelIds
            : defaultChannelId,
        metric: trigger ? trigger?.metric : defaultMetric,
        ...startAndEndComparePeriodDate,
    };

    if (options.variants.includes(MarketingActionVariantEnum.Offer)) {
        const offerCurrentValues = currentValues as MarketingActionInputValues &
            Pick<Offer, "scenarioSpecificActivationPayload">;
        scenarioBasicConfig.scenarioSpecificActivation = {
            peakScenarioEnabled: !isNil(
                offerCurrentValues?.scenarioSpecificActivationPayload?.peak
            ),
            firstSlotScenarioEnabled: !isNil(
                offerCurrentValues?.scenarioSpecificActivationPayload?.firstSlot
            ),
            peakScenarioUuid:
                offerCurrentValues?.scenarioSpecificActivationPayload?.peak ===
                Number.parseInt(NO_SCENARIO_ID, 10)
                    ? NO_SCENARIO_ID
                    : offerCurrentValues?.scenarios.find(
                          (scenario) =>
                              scenario.id ===
                              offerCurrentValues?.scenarioSpecificActivationPayload?.peak
                      )?.uuid,
            firstSlotScenarioUuid:
                offerCurrentValues?.scenarioSpecificActivationPayload?.firstSlot ===
                Number.parseInt(NO_SCENARIO_ID, 10)
                    ? NO_SCENARIO_ID
                    : offerCurrentValues?.scenarios.find(
                          (scenario) =>
                              scenario.id ===
                              offerCurrentValues?.scenarioSpecificActivationPayload?.firstSlot
                      )?.uuid,
        };
    }

    return scenarioBasicConfig;
};

const inputFormatCondition = (
    scenario: MarketingActionScenario,
    options: InputOptions,
    isPricesExvat: boolean
): MarketingActionConditionInput => {
    let triggerStartStatus: TriggerStatus;
    if (!scenario) {
        // default scenario
        triggerStartStatus = "enable";
    } else if (scenario?.triggerStartId && scenario?.triggerStart?.metaData) {
        // marketing action with trigger start
        triggerStartStatus = "enable";
    } else {
        // marketing action without trigger start
        triggerStartStatus = "disable";
    }

    let triggerStopStatus: TriggerStatus = "disable";
    if (scenario && scenario?.triggerStop?.metaData) {
        triggerStopStatus = "enable";
    }

    const correctTrigger = getCorrectTrigger(scenario?.triggerStart, scenario?.triggerStop);

    const defaultPeriod = AutoRulesComparePeriodEnum.lastFourWeeks;

    return {
        triggerStart: scenario?.triggerStartId
            ? {
                  ...scenario.triggerStart,
                  comparePeriod: scenario.triggerStart.comparePeriod ?? defaultPeriod,
                  metaData: scenario.triggerStart.metaData
                      ? {
                            ...scenario.triggerStart.metaData,
                            channelIds: correctTrigger?.metaData?.channelIds ?? [],
                            value:
                                scenario.triggerStart.metaData.value /
                                METRIC_FACTOR[scenario.triggerStart.metric],
                            thresholdFactor: scenario.triggerStart.metaData.thresholdFactor ?? 0,
                        }
                      : {
                            channelIds: correctTrigger?.metaData?.channelIds ?? [],
                            thresholdFactor: 0,
                        },
              }
            : {
                  comparePeriod: defaultPeriod,
                  metric: isPricesExvat
                      ? TriggerMetricEnum.DailySalesExvat
                      : TriggerMetricEnum.DailySales,
                  operator: TriggerOperatorEnum.LessThan,
                  metaData: defaultMetaDataValues(options.channelIds),
              },
        triggerStartStatus,
        triggerStopStatus,
        triggerStop: scenario?.triggerStop
            ? {
                  ...scenario.triggerStop,
                  comparePeriod: scenario.triggerStop.comparePeriod ?? defaultPeriod,
                  metaData: scenario.triggerStop.metaData
                      ? {
                            ...scenario.triggerStop.metaData,
                            channelIds: correctTrigger?.metaData?.channelIds ?? [],
                            value:
                                scenario.triggerStop.metaData.value /
                                METRIC_FACTOR[scenario.triggerStart.metric],
                            thresholdFactor: scenario.triggerStop.metaData.thresholdFactor ?? 0,
                        }
                      : {
                            channelIds: correctTrigger?.metaData?.channelIds ?? [],
                            thresholdFactor: 0,
                        },
              }
            : {
                  comparePeriod: scenario?.triggerStart
                      ? scenario.triggerStart.comparePeriod
                      : defaultPeriod,
                  metric: scenario?.triggerStart
                      ? scenario.triggerStart.metric
                      : TriggerMetricEnum.DailySales,
                  operator: TriggerOperatorEnum.MoreThan,
                  metaData: defaultMetaDataValues(options.channelIds),
              },
    };
};

export const inputFormatRecurrence = (
    values: MarketingActionInputValues,
    _countOnline: number,
    options: InputOptions
): MarketingActionRecurrenceInput => {
    const isStartedToday =
        getDateStringWithTimeZone(
            `${getNowWithTimeZone(options.user.timezone).format(FORMAT_DATE_STD)} ${formatDurationToTime(
                moment.duration(values?.durationStartFromMidnight),
                (string) => options.t(`${COMMON_TRANSLATION_NAMESPACE}:${string}`)
            )}`,
            options.user.timezone
        ).isBefore(getNowWithTimeZone(options.user.timezone)) &&
        moment(values?.startDate).isSame(moment(), "d");

    const startDate =
        values?.status === OfferStatus.Ongoing && isStartedToday
            ? getNowWithTimeZone(options.user.timezone)
            : moment(values?.startDate);
    const startDuration = values?.durationStartFromMidnight
        ? moment.duration(values.durationStartFromMidnight)
        : null;
    const endDuration = values?.durationEndFromMidnight
        ? moment.duration(values.durationEndFromMidnight)
        : null;

    const startTime = values
        ? formatDurationToTime(startDuration, (string) =>
              options.t(`${COMMON_TRANSLATION_NAMESPACE}:${string}`)
          )
        : null;

    const endTime =
        startDuration &&
        endDuration &&
        endDuration.asMilliseconds() > startDuration.asMilliseconds()
            ? formatDurationToTime(endDuration, (string) =>
                  options.t(`${COMMON_TRANSLATION_NAMESPACE}:${string}`)
              )
            : null;

    let currentFrequency =
        values && Object.values(recurrenceConfig).find((e) => e.id === values.recurrence?.id).key;
    let days: number[] =
        (values && currentFrequency === RecurrenceFrequence.SPE
            ? values.days
            : WEEKDAYS_PER_FREQUENCE[currentFrequency]) ?? [];
    if (currentFrequency === RecurrenceFrequence.SAMEDAYWEEKLY) {
        currentFrequency = RecurrenceFrequence.SPE;
        days = [startDate.day()];
    }
    if (currentFrequency === RecurrenceFrequence.ONETIME) {
        days = values?.days;
    }

    // Ancienne condition : Je comprends pas pourquoi la premiere partie de cette condition existe
    // il faut du coup bien bien tester que ca pète pas un truc mon changement mais on a pas réussi a trouver d'explication avec Wuillaume
    // je laisse le commentaire la le temps de la review dev :)
    // Bien tester les ending du coup pedant la review plz <3

    // const ending =
    //     (values?.recurrence.id === MarketingActionRecurrenceEnum.Once &&
    //         !values?.activationTypes.includes(MarketingActionActivationTypeEnum.Special)) ||
    //     (values?.startDate && !values?.endDate)
    //         ? "forever"
    //         : "date";

    const ending =
        values?.startDate &&
        !values?.endDate &&
        !values?.activationTypes.includes(MarketingActionActivationTypeEnum.Special)
            ? "forever"
            : "date";

    return {
        defaultDisabledField: [ending === "forever" && "recurrence.endDate"].filter(Boolean),
        ending,
        startTime: values
            ? formatHoursToSlot(startTime, (string) =>
                  options.t(`${COMMON_TRANSLATION_NAMESPACE}:${string}`)
              )
            : null,
        endTime: values
            ? formatHoursToSlot(endTime, (string) =>
                  options.t(`${COMMON_TRANSLATION_NAMESPACE}:${string}`)
              )
            : null,
        startDate:
            values &&
            (moment(startDate).isAfter(moment(), "d") ||
                options.variants.includes(MarketingActionVariantEnum.Special))
                ? moment(startDate)
                : moment(),
        endDate:
            !values ||
            (options.duplicate && !options.variants.includes(MarketingActionVariantEnum.Special)) ||
            !values.endDate
                ? null
                : moment(values.endDate),
        days: days.length ? days : ALL_DAYS_OF_THE_WEEK,
        frequence: values ? currentFrequency : RecurrenceFrequence.ALLDAY,
    };
    // return data;
};

export const inputFormatAudience = (
    values: MarketingActionInputValues,
    scenario: MarketingActionScenario
): Pick<MarketingActionScenarioCharacteristicInput, "audience"> => {
    if (
        values?.activationTypes.includes(MarketingActionActivationTypeEnum.Special) &&
        !(values?.mainScenario as OfferScenario)?.discount?.specialOperationTitle
    ) {
        return {
            audience: {
                value: "1",
            },
        };
    }
    return {
        audience: {
            value: values === null ? "1" : scenario.audience?.id.toString(),
        },
    };
};

export const inputFormatSponsoredAdCharacteristic = (
    values: MarketingActionInputValues
): SponsoredAdScenarioCharacteristicInput => {
    const scenario = ((values?.scenarios[0] && (values?.scenarios[0] as SponsoredAdScenario).bid
        ? values?.scenarios[0]
        : values?.duoMarketingAction?.scenarios[0]) as SponsoredAdScenario) ?? {
        bid: null,
        budget: null,
        audience: { options: [], value: "1" },
        slotBudget: 0,
        id: null,
    };

    const data = {
        ...optionsCharacteristics[values?.service || values?.channel?.source],
        isBidAutoEnabled: false,
        bid: {
            min: 0,
            max: 0,
            value: scenario.bid ?? null,
        },
        slotBudget: {
            ...optionsCharacteristics[values?.service || values?.channel?.source]?.budget,
            value: scenario.slotBudget ? scenario.slotBudget / PRICE_FACTOR : null,
        },
        weeklyBudget: {
            value: 0,
            min: 0,
            max: 0,
        },
        totalBudget: {
            value: 0,
            min: 0,
            max: 0,
        },
        budgetComputeMethod: BudgetComputeMethodEnum.Slot,
    };
    return data;
};

export const inputFormatOfferCharacteristic = (
    values: MarketingActionInputValues,
    options: InputOptions
): OfferScenarioCharacteristicInput => {
    let scenario = values?.scenarios[0] as OfferScenario;

    if (
        options.variants.includes(MarketingActionVariantEnum.SponsoredAd) &&
        options.variants.includes(MarketingActionVariantEnum.Duo)
    ) {
        scenario = values?.duoMarketingAction?.scenarios[0] as OfferScenario;
    }

    if (
        values?.activationTypes.includes(MarketingActionActivationTypeEnum.Special) &&
        !scenario?.discount?.specialOperationTitle
    ) {
        return {
            specialExistOnFlynt: scenario
                ? scenario?.discount?.isEnabled === true &&
                  scenario?.discount?.isSpecialDiscount === true
                    ? true
                    : false
                : null,
            isSpecialOperation:
                values === null
                    ? false
                    : values.activationTypes.includes(MarketingActionActivationTypeEnum.Special),
            isSpecialString:
                values === null ||
                !values.activationTypes.includes(MarketingActionActivationTypeEnum.Special)
                    ? "false"
                    : "true",
            specialOperation: null,
            isCoFunded:
                values.activationTypes.includes(MarketingActionActivationTypeEnum.CoFunded) ??
                false,
            ...{
                ...inputFormatAudience(values, scenario as MarketingActionScenario),
                value: scenario ? scenario.type : null,
                products: [],
                categoryItemList: [],
                cart: null,
                discount: null,
            },
        };
    }

    if (options.variants.includes(MarketingActionVariantEnum.Special) && values === null) {
        return {
            specialExistOnFlynt: null,
            isSpecialOperation: true,
            isSpecialString: "true",
            specialOperation: null,
            isCoFunded: false,
            isCreationSpecialOperation: true,
            ...{
                ...inputFormatAudience(values, scenario as MarketingActionScenario),
                value: scenario ? scenario.type : null,
                products: [],
                categoryItemList: [],
                cart: null,
                discount: null,
            },
        };
    }

    const result = {
        specialExistOnFlynt: scenario
            ? scenario?.discount?.isEnabled === true &&
              scenario?.discount?.isSpecialDiscount === true
                ? true
                : false
            : null,
        isSpecialOperation:
            values === null
                ? false
                : values.activationTypes.includes(MarketingActionActivationTypeEnum.Special),
        isSpecialString:
            values === null
                ? "false"
                : values.activationTypes
                      .includes(MarketingActionActivationTypeEnum.Special)
                      .toString(),
        specialOperation: scenario ? scenario.discount : null,
        isCoFunded:
            values?.activationTypes.includes(MarketingActionActivationTypeEnum.CoFunded) ?? false,
        ...{
            audience: { value: "1" },
            value: null,
            typeDiscount: null,
            products: [],
            categoryItemList: [],
            cart: null,
            discount: null,
        },
        ...{
            ...inputFormatAudience(values, scenario as MarketingActionScenario),
            value: scenario ? scenario.type : null,
            categoryItemList: scenario?.categoryItemList
                ? scenario.categoryItemList.map((elm) => {
                      return {
                          name: elm.name ?? UNKNOWN_CATEGORY_NAME,
                          itemList: (elm as any).items?.map((e) => {
                              return {
                                  name: e,
                              };
                          }),
                          itemListString: ((elm as any) ?? []).items.join(", "),
                          discount: elm.discount,
                          remoteCategory: {
                              name: elm.name ?? UNKNOWN_CATEGORY_NAME,
                          },
                      };
                  })
                : [],
            products: scenario
                ? options.notDiscountAvailableIndexList?.length
                    ? []
                    : uniqBy(
                          scenario?.items?.map((elm) => ({
                              ...elm,
                              discountOption:
                                  elm.discount.flatDiscount ?? elm.discount.percentDiscount,
                          })),
                          (v) => `${v.name}-${v.categoryName}`
                      )
                : [],
            cart: scenario
                ? options.notDiscountAvailableIndexList?.length
                    ? null
                    : scenario.discount?.minBasket
                : null,
            discount:
                scenario && scenario.discount
                    ? options.notDiscountAvailableIndexList?.length
                        ? null
                        : buildBaseCurrentKey(scenario.discount)
                    : null,
        },
    };
    return result;
};

// Smart scheduler - references values
export function inputFormatReferencesValues(
    values: MarketingActionInputValues
): Record<string, string | number>[] {
    const posMarketingActionActivatedList = !values
        ? []
        : values?.posMarketingActions.filter((e) => e.isActiveLink);
    return posMarketingActionActivatedList.map((posMarketingAction) => {
        const dayValues = ALL_DAYS_OF_THE_WEEK.reduce(
            (acc, day) =>
                [ServiceTime.Lunch, ServiceTime.Dinner].reduce((acc2, serviceTime) => {
                    const valueFounded = (
                        posMarketingAction.specificValuePayload as SpecificValueSmartSchedulerPayload[]
                    )?.find(
                        (item) => +item.dow === +day && +item.serviceTime === +serviceTime
                    )?.value;
                    if (valueFounded) {
                        return {
                            ...acc2,
                            [`${day}_${serviceTime}`]: !isNil(valueFounded)
                                ? valueFounded
                                : undefined,
                        };
                    }
                    return acc2;
                }, acc),
            {}
        );
        return {
            thresholdFactor: posMarketingAction.thresholdFactor,
            posId: posMarketingAction.pos.id,
            ...dayValues,
        };
    });
}

// Auto rules - references values
export function inputFormatReferencesValuesAutoRules(values: MarketingActionInputValues): any {
    const posMarketingActionActivatedList = !values
        ? []
        : values?.posMarketingActions.filter((e) => e.isActiveLink);
    return posMarketingActionActivatedList.map((posMarketingAction) => {
        return {
            posId: posMarketingAction.pos.id,
            ...posMarketingAction.specificValuePayload,
        };
    });
}

export const inputFormatServiceAndChannel = (
    values: MarketingActionInputValues
): MarketingActionServiceChannelInput => {
    if (values?.activationTypes.includes(MarketingActionActivationTypeEnum.Special)) {
        return {
            service: {
                value: values?.channel?.source ?? (values?.service as unknown as ChannelSourceEnum),
            },
            channel: {
                value: values === null ? null : ChannelCategory.Delivery,
            },
        };
    }
    return {
        service: {
            value: values?.channel?.source ?? (values?.service as unknown as ChannelSourceEnum),
        },
        channel: {
            value: values === null ? null : values?.channel?.category,
        },
    };
};

export const inputFormatModal = (): MarketingActionModalInput => {
    return {
        overrideStrategy: undefined,
        overrides: [],
        unavailablePosIds: [],
        invalidPosIds: [],
        confirmMissingProducts: false,
        confirmOpeningHours: false,
        confirmCashierUnsync: false,
        preorder: true,
    };
};

export const inputFormatOthers = (): MarketingActionOtherInput => {
    return {
        confirmOpeningHours: false,
        confirmCashierUnsync: false,
        confirm: false,
    };
};

const inputFormatScenarios = (
    scenario: OfferScenario,
    {
        index,
        variants,
        isTemplate = false,
        start,
        end,
        notDiscountAvailableIndexList = [],
    }: {
        index: number;
        variants: MarketingActionVariantEnum[];
        isTemplate?: boolean;
        start?: number;
        end?: number;
        notDiscountAvailableIndexList?: number[];
    }
): CustomScenario => {
    const data = {
        id: index,
        triggerValue: 0,
        triggerMin: isTemplate
            ? start
            : variants.includes(MarketingActionVariantEnum.SmartScheduler)
              ? addHundredDelta(scenario.triggerStart?.metaData?.thresholdFactor)
              : scenario.triggerStart?.metaData?.thresholdFactor,
        triggerMax: isTemplate
            ? end
            : scenario.triggerStop?.metaData === null ||
                scenario.triggerStop?.metaData === undefined
              ? MAX_EDGE_VALUE
              : variants.includes(MarketingActionVariantEnum.SmartScheduler)
                ? addHundredDelta(scenario.triggerStop?.metaData?.thresholdFactor)
                : scenario.triggerStop?.metaData?.thresholdFactor,

        audience: isTemplate ? 1 : scenario?.audience?.id,
        value: scenario.type,
        typeDiscount: scenario.discount?.percentDiscount
            ? OfferDiscountType.PercentDiscount
            : OfferDiscountType.FlatDiscount,
        discount:
            notDiscountAvailableIndexList.includes(index) || !scenario.discount
                ? null
                : buildBaseCurrentKey(scenario.discount),
        cart: notDiscountAvailableIndexList.includes(index) ? null : scenario.discount?.minBasket,
        isTemplate: notDiscountAvailableIndexList.includes(index) ? true : isTemplate,
        categoryItemList: scenario
            ? scenario?.categoryItemList?.map((elm) => {
                  return {
                      name: elm.name ?? UNKNOWN_CATEGORY_NAME,
                      itemList: (elm as any).items?.map((e) => {
                          return {
                              name: e,
                          };
                      }),
                      itemListString: ((elm as any) ?? []).items.join(", "),
                      discount: elm.discount,
                      remoteCategory: {
                          name: elm.name ?? UNKNOWN_CATEGORY_NAME,
                      },
                  };
              })
            : [],
        products:
            scenario && scenario.type === OfferTypeEnum.DiscountByItem
                ? notDiscountAvailableIndexList.includes(index)
                    ? []
                    : scenario?.items?.map((elm) => {
                          return {
                              ...elm,
                              discountOption:
                                  elm.discount?.percentDiscount ?? elm.discount?.flatDiscount,
                          };
                      })
                : (scenario?.items?.map((elm) => {
                      return {
                          ...elm,
                          discountOption:
                              elm.discount?.percentDiscount ?? elm.discount?.flatDiscount,
                      };
                  }) ?? []),
        uuid: scenario.uuid ?? uuid(),
        isHover: false,
    };
    return data;
};

export const INITIAL_TEMPLATE_EDGES = [
    { start: 0, end: 25 },
    { start: 25, end: 50 },
    { start: 50, end: 75 },
];

export const generateDefaultEdgesScenario = (
    variants: MarketingActionVariantEnum[]
): CustomScenario[] => {
    return INITIAL_TEMPLATE_EDGES.map((e, index) =>
        inputFormatScenarios(e as any, {
            index,
            variants,
            isTemplate: true,
            start: e.start,
            end: e.end,
        })
    );
};

export const inputFormatMarketingAction = (
    values: {
        offer?: Offer;
        sponsoredAd?: SponsoredAd;
        countOnlineSlots: number;
    } | null,
    baseOptions: InputOptions,
    isPricesExvat: boolean
): MarketingActionFormInput => {
    const options = {
        ...baseOptions,
        notDiscountAvailableIndexList: [],
    };
    let currentValues: MarketingActionInputValues = null;
    const isOffer =
        (options.variants.includes(MarketingActionVariantEnum.Offer) ||
            options.variants.includes(MarketingActionVariantEnum.Duo)) &&
        values?.offer;
    if (isOffer) {
        const offer = extendMarketingActionWithMainScenario(values.offer);
        currentValues = {
            ...offer,
            originalOfferId: offer.id,
            originalSponsoredAdId: offer?.duoMarketingAction?.id,
            duo: offer?.duoMarketingAction,
        };
    }

    if (
        (options.variants.includes(MarketingActionVariantEnum.SponsoredAd) ||
            options.variants.includes(MarketingActionVariantEnum.Duo)) &&
        values?.sponsoredAd
    ) {
        const sponsoredAd = extendMarketingActionWithMainScenario(values.sponsoredAd);
        currentValues = {
            ...sponsoredAd,
            originalSponsoredAdId: sponsoredAd.id,
            originalOfferId: sponsoredAd?.duoMarketingAction?.id,
            duo: sponsoredAd?.duoMarketingAction,
            channel: sponsoredAd?.duoMarketingAction?.channel,
        };
    }

    if (options.duplicate || options.update) {
        if (options.duplicate) {
            delete currentValues?.id;
        }

        if (isOffer) {
            const sources = options?.channelSources ?? [];
            const source = currentValues?.channel?.source;
            const category = currentValues?.channel?.category;

            if (values) {
                options.notDiscountAvailableIndexList = currentValues?.scenarios.reduce(
                    (acc, scenario, index) => {
                        const audienceId = +scenario?.audience?.id;
                        const currentDiscountList = getDiscounts(
                            sources,
                            source,
                            audienceId,
                            category
                        )
                            .filter((e) => e.isEnabled)
                            .map((e) => e.id);
                        const offerScenario = scenario as OfferScenario;
                        const isAvailable =
                            offerScenario.items?.length &&
                            offerScenario.type === OfferTypeEnum.DiscountByItem
                                ? offerScenario.items.every((item) =>
                                      currentDiscountList.includes(item.discountId)
                                  )
                                : currentDiscountList.includes(offerScenario.discountId);
                        if (!isAvailable) {
                            acc.push(index);
                        }
                        return acc;
                    },
                    []
                );
                if (options.notDiscountAvailableIndexList.length) {
                    options.enqueueSnackbar(options.t("old-discount"), { variant: "warning" });
                }
            }
        }
    }

    if (currentValues) {
        currentValues = {
            ...currentValues,
            scenarios: currentValues.scenarios?.map((scenario) => ({
                ...scenario,
                uuid: uuid(),
                isHover: false,
            })),
        };

        if (options.variants.includes(MarketingActionVariantEnum.AutoRule)) {
            const activationTypes = values.offer
                ? values.offer.activationTypes
                : values.sponsoredAd.activationTypes;
            currentValues = {
                ...currentValues,
                pushTriggerMode:
                    activationTypes && activationTypes.length > 0
                        ? activationTypes.includes(MarketingActionActivationTypeEnum.NonParallel)
                            ? PushTriggerModeEnum.NON_PARALLEL
                            : PushTriggerModeEnum.PARALLEL
                        : PushTriggerModeEnum.NON_PARALLEL,
            };
        }
    }

    const offer = {
        ...inputFormatAudience(currentValues, currentValues?.scenarios[0]),
        scenarios:
            currentValues?.scenarios.length > 0
                ? currentValues?.scenarios.map((e, index) =>
                      inputFormatScenarios(e as OfferScenario, {
                          index,
                          variants: options.variants,
                          notDiscountAvailableIndexList: options.notDiscountAvailableIndexList,
                      })
                  )
                : generateDefaultEdgesScenario(options.variants),
        scenarioBasicConfig: inputFormatScenarioBasicConfig(currentValues, options, isPricesExvat),
        originalSponsoredAdId: !options.duplicate ? currentValues?.originalSponsoredAdId : null,
        originalOfferId: !options.duplicate ? currentValues?.originalOfferId : null,
        posList: inputFormatPos(currentValues),
        originalPoss: !options.duplicate ? inputFormatOriginalPos(currentValues) : null,
        recurrence: inputFormatRecurrence(currentValues, values?.countOnlineSlots, options),
        sponsoredAdCharacteristic: inputFormatSponsoredAdCharacteristic(currentValues),
        offerCharacteristic: inputFormatOfferCharacteristic(currentValues, options),
        serviceAndChannel: inputFormatServiceAndChannel(currentValues),
        condition: inputFormatCondition(currentValues?.scenarios[0], options, isPricesExvat),
        modal: inputFormatModal(),
        confirmOpeningHours: inputFormatOthers().confirmOpeningHours,
        confirmCashierUnsync: inputFormatOthers().confirmCashierUnsync,
        confirm: inputFormatOthers().confirm,
        specificValueSmartSchedulerPayload: inputFormatReferencesValues(currentValues),
        specificValueAutoRulePayload: inputFormatReferencesValuesAutoRules(currentValues),
        pushTriggerMode: currentValues?.pushTriggerMode ?? PushTriggerModeEnum.NON_PARALLEL,
    };

    return offer;
};

//#endregion

//#region OUTPUTS

const buildScenarioTriggers = (scenario, scenarioBasicConfig) => {
    const commonValues = {
        comparePeriod: scenarioBasicConfig.comparePeriod,
        metric: scenarioBasicConfig.metric,
        operator: TriggerOperatorEnum.MoreThan,
    };

    const triggerStart = {
        ...commonValues,
        metaData: {
            thresholdFactor: subtractHundredDelta(scenario.triggerMin),
            channelIds: scenarioBasicConfig.channelIds,
            value: 0,
            startDateComparePeriod: scenarioBasicConfig.startDateComparePeriod,
            endDateComparePeriod: scenarioBasicConfig.endDateComparePeriod,
        },
    };

    const triggerStop = {
        ...commonValues,
        metaData:
            scenario.triggerMax === MAX_EDGE_VALUE
                ? null
                : {
                      thresholdFactor: subtractHundredDelta(scenario.triggerMax),
                      channelIds: scenarioBasicConfig.channelIds,
                      value: 0,
                      startDateComparePeriod: scenarioBasicConfig.startDateComparePeriod,
                      endDateComparePeriod: scenarioBasicConfig.endDateComparePeriod,
                  },
    };
    return {
        triggerStart,
        triggerStop,
    };
};

const buildRealScenario = (
    values: MarketingActionFormInput,
    scenario: CustomScenario,
    scenarioBasicConfig: any,
    options: OutputOptions
): MarketingActionScenarioOutput => {
    const { triggerStart, triggerStop } = buildScenarioTriggers(scenario, scenarioBasicConfig);
    const sources = options.channelSources;
    const source = values.serviceAndChannel.service.value;
    const audienceId = +scenario.audience;
    const category = values.serviceAndChannel.channel.value;

    /**
     *   KNOWN PROBLEM : for discount type that applies the same discount to multiple items (e.g. free-item, specific-dish...)
     *   The discountId returned for the scenario could be different from the one assigned to each item in the scenario.items array.
     */

    const discountId = getDiscounts(sources, source, audienceId, category).find((d) => {
        const dClassicKey = buildBaseCurrentKey(d);
        return (
            dClassicKey === scenario.discount &&
            d.minBasket === scenario.cart &&
            d.isSpecialDiscount === false
        );
    })?.id;
    const returnValues = {
        type: scenario.value as OfferTypeEnum,
        audience: +scenario.audience,
        triggerStart,
        triggerStop,
        items: scenario.products.map((elm) => ({
            name: elm.name,
            discount:
                getDiscounts(
                    options.channelSources,
                    values.serviceAndChannel.service.value,
                    +scenario.audience,
                    values.serviceAndChannel.channel.value
                ).find((d) => {
                    return (
                        d.type === scenario.value &&
                        (d.flatDiscount === elm.discountOption ||
                            d.percentDiscount === elm.discountOption)
                    );
                })?.id ?? discountId,
            categoryName:
                (elm as any)?.remoteCategory?.name ?? elm.categoryName ?? UNKNOWN_CATEGORY_NAME,
        })),
        categoryItemList: scenario.categoryItemList
            ? scenario.categoryItemList.map((elm) => {
                  return {
                      name: elm.name,
                      itemList: elm.itemList?.map((e) => e.name),
                      discount:
                          getDiscounts(
                              options.channelSources,
                              values.serviceAndChannel.service.value,
                              +scenario.audience,
                              values.serviceAndChannel.channel.value
                          ).find((d) => {
                              return (
                                  d.type === scenario.value &&
                                  (d.flatDiscount === elm.discountOption ||
                                      d.percentDiscount === elm.discountOption)
                              );
                          })?.id ?? discountId,
                  };
              })
            : [],
        discount: discountId,
        uuid: scenario.uuid,
    };

    return returnValues;
};

export const outputFormatPos = (
    values: MarketingActionFormInput,
    options: OutputOptions
): Pick<MarketingActionFormOutput, "poss"> => {
    const posValues = getCorrectPosValue(values, options.posListOptions, options.allOption);
    return { poss: posValues.map((pos) => pos.id) };
};

export const outputFormatOriginalPos = (
    values: MarketingActionFormInput
): Pick<MarketingActionFormOutput, "originalPoss"> => {
    return { originalPoss: values.originalPoss };
};

const buildSmartSchedulerSpecificValues = (values): SpecificValueSmartSchedulerPayload[] => {
    const finalValues = values?.specificValues?.reduce((acc, reference) => {
        const values = [];

        Object.entries(reference).forEach(([key, value]) => {
            if (
                [
                    "maximum",
                    "metric",
                    "pos",
                    "posId",
                    "thresholdFactor",
                    "isEditedDowNumberListLunch",
                    "isEditedDowNumberListDinner",
                ].includes(key) === false
            ) {
                values.push({
                    dow: key.split("_")[0],
                    serviceTime: key.split("_")[1],
                    value: value,
                });
            }
        });
        acc.push({
            posId: reference.posId,
            thresholdFactor: reference.thresholdFactor,
            values: values,
        });

        return acc;
    }, []);

    return finalValues;
};

export const outputFormatOfferSmartScheduler = (
    values: MarketingActionFormInput,
    options: OutputOptions
): Pick<
    OfferFormOutput,
    "isSpecial" | "isCoFunded" | "scenarios" | "scenarioSpecificActivation" | "specificValues"
> => {
    const allRealScenarios = values.scenarios.map((scenario) =>
        buildRealScenario(values, scenario, values.scenarioBasicConfig, options)
    );

    return {
        isSpecial: false,
        isCoFunded: false,
        scenarios: allRealScenarios,
        specificValues:
            values.scenarioBasicConfig.comparePeriod === AutoRulesComparePeriodEnum.selection
                ? buildSmartSchedulerSpecificValues(values)
                : undefined,
        scenarioSpecificActivation: values.scenarioBasicConfig.scenarioSpecificActivation,
    };
};

export const outputFormatCondition = (
    values: MarketingActionFormInput,
    { variants }: { variants: MarketingActionVariantEnum[] }
): TriggerCharacteristicOutput => {
    const scenario = values.condition;
    const scenarioBasic = values.scenarioBasicConfig;

    if (!variants.includes(MarketingActionVariantEnum.AutoRule)) {
        return {
            triggerStart: null,
            triggerStopStatus: null,
            triggerStop: null,
            triggerStartStatus: null,
        };
    }

    return {
        triggerStart: {
            comparePeriod: scenarioBasic?.comparePeriod,
            metric: scenario?.triggerStart?.metric,
            operator: scenario?.triggerStart?.operator,
            metaData:
                scenario?.triggerStartStatus === "enable"
                    ? {
                          ...scenario?.triggerStart?.metaData,
                          startDateComparePeriod: scenarioBasic.startDateComparePeriod,
                          endDateComparePeriod: scenarioBasic.endDateComparePeriod,
                          value:
                              scenario?.triggerStart?.metaData?.value *
                              METRIC_FACTOR[scenario?.triggerStart.metric],
                          thresholdFactor: scenario?.triggerStart?.metaData?.thresholdFactor,
                      }
                    : null,
        },
        triggerStopStatus: scenario?.triggerStopStatus,
        triggerStartStatus: scenario?.triggerStartStatus,
        triggerStop: {
            comparePeriod: scenarioBasic?.comparePeriod,
            metric: scenario?.triggerStop?.metric,
            operator: scenario?.triggerStop?.operator,
            metaData:
                scenario?.triggerStopStatus === "enable"
                    ? {
                          ...scenario?.triggerStop?.metaData,
                          startDateComparePeriod: scenarioBasic.startDateComparePeriod,
                          endDateComparePeriod: scenarioBasic.endDateComparePeriod,
                          value:
                              scenario?.triggerStop?.metaData?.value *
                              METRIC_FACTOR[scenario?.triggerStop.metric],
                          thresholdFactor: scenario?.triggerStop?.metaData?.thresholdFactor,
                      }
                    : null,
        },
    };
};

export const outputFormatRecurrence = (
    values: MarketingActionFormInput,
    recurrenceConfig: RecurrenceConfig
): Pick<
    MarketingActionFormOutput,
    | "startDate"
    | "endDate"
    | "durationStartFromMidnight"
    | "durationEndFromMidnight"
    | "now"
    | "days"
    | "recurrence"
    | "isWeekly"
> => {
    return {
        startDate: moment(values.recurrence.startDate).format(FORMAT_DATE_STD),
        endDate: values.recurrence.endDate
            ? moment(values.recurrence.endDate).format(FORMAT_DATE_STD)
            : null,
        durationStartFromMidnight: formatSlotToDuration(values.recurrence.startTime).toISOString(),
        durationEndFromMidnight: formatSlotToDuration(values.recurrence.endTime).toISOString(),
        now: 0,
        days: values.recurrence.frequence === RecurrenceFrequence.SPE ? values.recurrence.days : [],
        recurrence: recurrenceConfig[values.recurrence.frequence].id,
        isWeekly: recurrenceConfig[values.recurrence.frequence].isWeekly,
    };
};

export const outputFormatAudience = (
    values: MarketingActionScenarioCharacteristicInput
): Pick<MarketingActionScenarioCharacteristicOutput, "audience"> => {
    return {
        audience: +values.audience.value,
    };
};

function getDiscounts(channelSources, source, audienceId, category) {
    return channelSources
        .find((channelSource) => channelSource.source === source)
        .offerAudiences.find((offerAudience) => offerAudience.id === audienceId)
        .categoryDiscounts.find((categoryDiscount) => {
            return categoryDiscount.category === category;
        }).discounts;
}

const buildAutoRulesSpecificValues = (values): SpecificValueAutoRulePayload[] => {
    const finalValues = values?.specificValues?.reduce((acc, reference) => {
        const startArray = [];
        const endArray = [];

        Object.entries(reference?.start ?? []).forEach(([key, value]) => {
            if (key !== "isEditedDowNumberList") {
                startArray.push({
                    dow: Number.parseInt(key.split("_")[1], 10),
                    value,
                    trigger: PositionEnum.Start,
                });
            }
        });

        Object.entries(reference?.stop ?? []).forEach(([key, value]) => {
            if (key !== "isEditedDowNumberList") {
                endArray.push({
                    dow: Number.parseInt(key.split("_")[1], 10),
                    value,
                    trigger: PositionEnum.Stop,
                });
            }
        });

        acc.push({
            posId: reference.posId,
            values: [...startArray, ...endArray],
        });

        return acc;
    }, []);

    return finalValues;
};

export const outputFormatOfferCharacteristics = (
    values: MarketingActionFormInput,
    options: OutputOptions
): Pick<OfferFormOutput, "isSpecial" | "isCoFunded" | "scenarios" | "specificValues"> => {
    const isSpecial = values.offerCharacteristic.isSpecialString === "true" ? true : false;
    const scenario = values.offerCharacteristic;
    const audienceId = Number(values.audience.value);
    const sources = options.channelSources;
    const source = values.serviceAndChannel.service.value;
    const category = values.serviceAndChannel.channel.value;
    const discounts = getDiscounts(sources, source, audienceId, category);
    const discountId = discounts.find((d) => {
        const dClassicKey = buildBaseCurrentKey(d);
        if (isSpecial) {
            const specialDiscount = getDiscounts(sources, source, audienceId, category).find(
                (dd) => dd.id === values.offerCharacteristic?.specialOperation?.id
            );
            return (
                dClassicKey === values.offerCharacteristic.discount &&
                d.minBasket === scenario.cart &&
                d.isSpecialDiscount === true &&
                d.specialOperationTitle === specialDiscount.specialOperationTitle &&
                d.specialOperationSubTitle === specialDiscount.specialOperationSubTitle
            );
        } else {
            return (
                dClassicKey === values.offerCharacteristic.discount &&
                d.minBasket === scenario.cart &&
                d.isSpecialDiscount === false
            );
        }
    })?.id;

    return {
        isSpecial,
        isCoFunded: values.offerCharacteristic.isCoFunded,
        specificValues:
            values.scenarioBasicConfig.comparePeriod === AutoRulesComparePeriodEnum.selection
                ? buildAutoRulesSpecificValues(values)
                : undefined,
        scenarios: [
            {
                ...outputFormatCondition(values, options),
                ...outputFormatAudience(values),
                // Format audience
                type: scenario.value,
                items: scenario.products.map((elm) => ({
                    name: elm.name,
                    categoryName: (elm as any)?.remoteCategory?.name ?? UNKNOWN_CATEGORY_NAME,
                    discount:
                        getDiscounts(sources, source, audienceId, category).find((d) => {
                            return (
                                d.type === values.offerCharacteristic.value &&
                                (d.flatDiscount === elm.discountOption ||
                                    d.percentDiscount === elm.discountOption)
                            );
                        })?.id ?? discountId,
                })),
                categoryItemList: scenario.categoryItemList
                    ? scenario.categoryItemList.map((elm) => {
                          return {
                              name: elm.name,
                              itemList: elm.itemList?.map((e) => e.name),
                              discount:
                                  getDiscounts(sources, source, audienceId, category).find((d) => {
                                      return (
                                          d.type === values.offerCharacteristic.value &&
                                          (d.flatDiscount === elm.discountOption ||
                                              d.percentDiscount === elm.discountOption)
                                      );
                                  })?.id ?? discountId,
                          };
                      })
                    : [],
                discount: discountId,
            },
        ],
    };
};

export const outputFormatSponsoredAdCharacteristics = (
    values: MarketingActionFormInput,
    options: OutputOptions
): Pick<SponsoredAdFormOutput, "scenarios" | "specificValues"> => {
    return {
        specificValues:
            values.scenarioBasicConfig.comparePeriod === AutoRulesComparePeriodEnum.selection
                ? buildAutoRulesSpecificValues(values)
                : undefined,
        scenarios: [
            {
                ...outputFormatCondition(values, options),
                ...outputFormatAudience(values),
                slotBudget:
                    Math.round(values.sponsoredAdCharacteristic.slotBudget.value) * PRICE_FACTOR,
                bid: Math.round(values.sponsoredAdCharacteristic.bid.value),
            },
        ],
    };
};

export const outputFormatChannel = (
    values: MarketingActionFormInput
): Pick<OfferFormOutput, "category"> => ({
    category: values.serviceAndChannel.channel.value,
});

export const outputFormatService = (
    values: MarketingActionFormInput
): Pick<MarketingActionFormOutput, "source"> => ({
    source: values.serviceAndChannel.service.value,
});

export const outputFormatModalValues = (
    values: MarketingActionFormInput,
    options: OutputOptions
): Pick<
    MarketingActionFormOutput,
    | "overrideStrategy"
    | "unavailablePosIds"
    | "confirmMissingProducts"
    | "confirmOpeningHours"
    | "confirmCashierUnsync"
    | "preorder"
    | "invalidPosIds"
> => ({
    overrideStrategy: values.modal.overrideStrategy,
    unavailablePosIds: values.modal.unavailablePosIds ?? [],
    confirmMissingProducts: values.modal.confirmMissingProducts,
    confirmCashierUnsync: values.modal.confirmCashierUnsync,
    confirmOpeningHours: values.modal.confirmOpeningHours,
    preorder:
        options.blockPreorder === true
            ? options.variants.includes(MarketingActionVariantEnum.AutoRule)
                ? true
                : false
            : values.modal.preorder,
    invalidPosIds: values.modal.invalidPosIds,
});

export const outputFormatTriggerAverage = (
    values: MarketingActionFormInput,
    variants: MarketingActionVariantEnum[]
): TriggerRuleAverage => {
    // TODO: revoir l'envoi du trigger average avec les nouveaux scénarios

    const commonValues = {
        posIds: values?.posList ? values.posList.map((pos) => pos.id) : [],
        durationStartFromMidnight: values?.recurrence.startTime
            ? formatSlotToDuration(values.recurrence.startTime).toISOString()
            : "",
        durationEndFromMidnight: values?.recurrence.endTime
            ? formatSlotToDuration(values.recurrence.endTime).toISOString()
            : "",
        startDate: values.recurrence.startDate
            ? moment(values.recurrence.startDate).format(FORMAT_DATE_STD)
            : "",
        endDate: "", // pour le moment on préfère tout calculer même si l'offre n'est que sur un jour pour éviter d'appeler le back en cas de modification de dates.
        recurrence: recurrenceConfig[values.recurrence.frequence].id,
        days: [0, 1, 2, 3, 4, 5, 6],
        isSmartScheduler: variants?.includes(MarketingActionVariantEnum.SmartScheduler),
        startDateComparePeriod: values?.scenarioBasicConfig?.startDateComparePeriod,
        endDateComparePeriod: values?.scenarioBasicConfig?.endDateComparePeriod,
    };

    if (variants?.includes(MarketingActionVariantEnum.AutoRule)) {
        return {
            ...commonValues,
            channelIds:
                getFilledTrigger(values?.condition?.triggerStart, values?.condition?.triggerStop)
                    ?.metaData?.channelIds ?? [],
            autoRulesComparePeriod: values?.condition?.triggerStart?.comparePeriod,
            triggerMetric: values?.condition?.triggerStart?.metric,
        };
    } else if (variants?.includes(MarketingActionVariantEnum.SmartScheduler)) {
        return {
            ...commonValues,
            channelIds: values?.scenarioBasicConfig?.channelIds ?? [],
            autoRulesComparePeriod: values?.scenarioBasicConfig?.comparePeriod,
            triggerMetric: values?.scenarioBasicConfig?.metric,
        };
    } else {
        return {
            ...commonValues,
            channelIds: [],
            autoRulesComparePeriod: undefined,
            triggerMetric: undefined,
        };
    }
};

export const outputFormatTriggerMode = (
    values: MarketingActionFormInput
): Pick<MarketingActionFormOutput, "pushTriggerMode"> => ({
    pushTriggerMode: values.pushTriggerMode,
});

//#endregion
